import type { Length } from '@gitbook/api-client';
import {
    CHILD_OBJECT_INVALID,
    CHILD_REQUIRED,
    PARENT_OBJECT_INVALID,
    PARENT_TYPE_INVALID,
    Schema,
} from '@gitbook/slate';

import { BLOCK_COLUMN, BLOCK_COLUMNS, BLOCK_IF } from '../types';
import { COLUMN_CONTENT_BLOCKS, unwrapChildren, wrapInlinesInParagraph } from '../utils';
import { validateLength } from '../utils/validateLength';

export const MAX_COLUMNS = 4;
export const MIN_COLUMNS = 2;

export const schema = Schema.create({
    blocks: {
        [BLOCK_COLUMNS]: {
            parent: {
                objects: ['document', 'block'],
                types: [undefined, BLOCK_IF],
            },
            nodes: [{ types: [BLOCK_COLUMN], min: MIN_COLUMNS, max: MAX_COLUMNS }],
            isVoid: false,
            data: {
                // @deprecated Per-block width is no longer consumed by the editor or renderer
                fullWidth: (fullWidth) => fullWidth === undefined || typeof fullWidth === 'boolean',
            },
            normalize: Schema.createNormalize({
                [PARENT_OBJECT_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [PARENT_TYPE_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [CHILD_REQUIRED]: (change, context) => unwrapChildren(change, context.node),
            }),
        },
        [BLOCK_COLUMN]: {
            parent: { types: [BLOCK_COLUMNS] },
            nodes: [
                {
                    objects: ['block'],
                    types: COLUMN_CONTENT_BLOCKS,
                    min: 1,
                },
            ],
            data: {
                width: (width) => typeof width === 'undefined' || validatePercentage(width),
                verticalAlignment: (value) =>
                    value === undefined ||
                    value === 'top' ||
                    value === 'middle' ||
                    value === 'bottom',
            },
            normalize: Schema.createNormalize({
                [PARENT_TYPE_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [PARENT_OBJECT_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [CHILD_OBJECT_INVALID]: (change, context) => {
                    if (!('key' in context.node)) {
                        return;
                    }
                    return wrapInlinesInParagraph(change, context.node.key);
                },
            }),
        },
    },
});

function isLength(value: unknown): value is Length {
    return validateLength(value);
}
/**
 * Column width sizing only works with percentage values.
 */
function validatePercentage(length: unknown): boolean {
    if (!isLength(length)) {
        return false; // Invalid length format
    }
    if (typeof length === 'number') {
        return false; // Only percentage values are allowed
    }
    if (length.unit === '%') {
        return length.value >= 0 && length.value <= 100;
    }
    return false; // Only validate percentage unit
}
